import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronLeft,
  faRightFromBracket,
  faCashRegister,
  faArrowRightArrowLeft,
  faCircleDollarToSlot,
  faBoxesStacked,
  faUsers,
  faUserGroup,
  faMapLocationDot,
  faChartPie,
  faGear,
  faWarehouse,
  faBagShopping,
  faKeyboard,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

import FunctionKeysModal from './function-keys-modal';
import { setDestroyAuth, setLoggedInUser } from 'redux/slices/session.slice';
import { addToCart } from 'redux/slices/cart.slice';
import { routes } from 'utils';
import { _Object } from 'utils/interfaces';
import { AppDispatch } from 'redux/store';

const Sidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const { me } = useSelector((state: _Object) => state.session);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [isMobileOpen, setIsMobileOpen] = useState<boolean>(false);

  useEffect(() => {
    dispatch(setLoggedInUser());
  }, [dispatch]);

  // Listen for global toggle-sidebar event (from mobile header hamburger)
  useEffect(() => {
    const handleExternalToggle = () => {
      if (window.innerWidth < 768) {
        setIsMobileOpen((prev) => !prev);
      } else {
        setIsCollapsed((prev) => {
          const next = !prev;
          localStorage.setItem('sidebar_collapsed', String(next));
          return next;
        });
      }
    };
    window.addEventListener('toggle-sidebar', handleExternalToggle);
    return () => window.removeEventListener('toggle-sidebar', handleExternalToggle);
  }, []);

  // Auto-close drawer on mobile when route changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setIsMobileOpen(false);
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    if (window.innerWidth < 768) {
      setIsMobileOpen((prev) => !prev);
    } else {
      setIsCollapsed((prev) => {
        const next = !prev;
        localStorage.setItem('sidebar_collapsed', String(next));
        return next;
      });
    }
  };

  const destroyAuth = () => {
    dispatch(setDestroyAuth());
    dispatch(addToCart([]));
    localStorage.clear();
    navigate(`${routes.login}`);
  };

  const getStoreName = () => {
    return me?.client?.name || 'Ashoka Mart';
  };

  const getStoreSubtitle = () => {
    return me?.location?.name || me?.client?.city || me?.client?.address || 'Guna, MP';
  };

  // All navigation items from previous bottom footer & more modal
  const navItems = [
    {
      id: 'checkout',
      label: me?.role === 'super_admin' ? t('Clients') : (t('checkout.checkout') || 'Checkout'),
      path: me?.role === 'super_admin' ? routes.clients : routes.sales,
      isActive:
        location.pathname.startsWith(routes.sales) ||
        location.pathname === routes.clients ||
        location.pathname.startsWith(routes.createCustomer),
      icon: <FontAwesomeIcon icon={faCashRegister} />,
    },
    {
      id: 'orders',
      label: t('order.orders') || 'Orders',
      path: routes.orders,
      isActive: location.pathname.startsWith(routes.orders),
      icon: <FontAwesomeIcon icon={faArrowRightArrowLeft} />,
    },
    {
      id: 'products',
      label: t('product.products') || 'Products',
      path: routes.products,
      isActive:
        location.pathname.startsWith(routes.products) ||
        location.pathname.startsWith(routes.categories) ||
        location.pathname.startsWith(routes.brands) ||
        location.pathname.startsWith(routes.suppilers),
      icon: <FontAwesomeIcon icon={faBoxesStacked} />,
    },
    {
      id: 'expense',
      label: t('common.expense') || 'Expense',
      path: routes.expense,
      isActive:
        location.pathname.startsWith(routes.expense) ||
        location.pathname.startsWith(routes.expenseCategories),
      icon: <FontAwesomeIcon icon={faCircleDollarToSlot} />,
    },
    {
      id: 'customers',
      label: t('customer.customers') || 'Customers',
      path: routes.customers,
      isActive:
        location.pathname.startsWith(routes.customers) ||
        location.pathname.startsWith(routes.createCustomers),
      icon: <FontAwesomeIcon icon={faUsers} />,
    },
    {
      id: 'inventory',
      label: t('inventory.inventory') || 'Inventory',
      path: routes.inventory,
      isActive: location.pathname.startsWith(routes.inventory),
      icon: <FontAwesomeIcon icon={faWarehouse} />,
    },
    {
      id: 'locations',
      label: t('location.locations') || 'Locations',
      path: routes.locations,
      isActive: location.pathname.startsWith(routes.locations),
      icon: <FontAwesomeIcon icon={faMapLocationDot} />,
    },
    {
      id: 'users',
      label: t('user.users') || 'Users',
      path: routes.users,
      isActive: location.pathname.startsWith(routes.users),
      icon: <FontAwesomeIcon icon={faUserGroup} />,
    },
    {
      id: 'analytics',
      label: t('report.analytics') || 'Analytics',
      path: routes.analytics,
      isActive: location.pathname.startsWith(routes.analytics),
      icon: <FontAwesomeIcon icon={faChartPie} />,
    },
    {
      id: 'settings',
      label: t('common.settings') || 'Settings',
      path: routes.settings,
      isActive:
        location.pathname.startsWith(routes.settings) ||
        location.pathname.startsWith(routes.taxRates),
      icon: <FontAwesomeIcon icon={faGear} />,
    },
    {
      id: 'purchases',
      label: t('Purchase Report') || 'Purchases',
      path: routes.purchaseReport,
      isActive: location.pathname.startsWith(routes.purchaseReport),
      icon: <FontAwesomeIcon icon={faBagShopping} />,
    },
  ];

  return (
    <>
      <aside className={`app-sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        {/* Collapse / Expand Toggle Button centered on the border (DESKTOP ONLY) */}
        <button
          type="button"
          className="sidebar-collapse-toggle btn p-0 border-0 d-none d-md-flex"
          onClick={toggleSidebar}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
        </button>

        {/* Sidebar Header: Logo / Store Name + Mobile Close Button */}
        <div className="sidebar-header d-flex align-items-center justify-content-between">
          <div className="sidebar-brand d-flex align-items-center flex-grow-1 min-w-0">
            <div className="sidebar-brand-icon flex-shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l2-5h14l2 5v2a3 3 0 0 1-5 2 3 3 0 0 1-5-2 3 3 0 0 1-3-4z" />
                <path d="M5 13v8h14v-8" />
                <path d="M10 21v-6h4v6" />
              </svg>
            </div>
            {(!isCollapsed || isMobileOpen) && (
              <div className="sidebar-brand-text ms-2 min-w-0">
                <div className="brand-title text-truncate">{getStoreName()}</div>
                <div className="brand-subtitle text-truncate">{getStoreSubtitle()}</div>
              </div>
            )}
          </div>

          {/* Close button for mobile drawer */}
          <button
            type="button"
            className="btn btn-link p-0 text-white-50 d-md-none border-0 ms-2"
            onClick={() => setIsMobileOpen(false)}
            aria-label="Close menu"
            style={{ fontSize: '18px', lineHeight: 1 }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Sidebar Menu Items */}
        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {navItems.map((item) => (
              <li key={item.id} className="sidebar-menu-item">
                <Link
                  to={item.path}
                  className={`sidebar-link ${item.isActive ? 'active' : ''}`}
                  title={isCollapsed && !isMobileOpen ? item.label : undefined}
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.innerWidth < 768) {
                      setIsMobileOpen(false);
                    }
                  }}
                >
                  <span className="sidebar-icon">{item.icon}</span>
                  {(!isCollapsed || isMobileOpen) && <span className="sidebar-label">{item.label}</span>}
                </Link>
              </li>
            ))}

            {/* Function Keys / Shortcuts Trigger directly below Purchase Report */}
            <li className="sidebar-menu-item">
              <button
                type="button"
                className="sidebar-link border-0 bg-transparent w-100 text-start"
                data-bs-toggle="modal"
                data-bs-target="#functionKeysModal"
                title={isCollapsed && !isMobileOpen ? (t('Function Keys') || 'Function Keys') : undefined}
                aria-label={t('Function Keys') || 'Function Keys'}
                onClick={() => {
                  if (typeof window !== 'undefined' && window.innerWidth < 768) {
                    setIsMobileOpen(false);
                  }
                }}
              >
                <span className="sidebar-icon">
                  <FontAwesomeIcon icon={faKeyboard} />
                </span>
                {(!isCollapsed || isMobileOpen) && (
                  <span className="sidebar-label">
                    {t('Function Keys') || 'Function Keys'}
                  </span>
                )}
              </button>
            </li>
          </ul>
        </nav>

        {/* Sidebar Footer: Only Logout Button */}
        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-logout-btn btn border-0 w-100"
            onClick={destroyAuth}
            title={isCollapsed && !isMobileOpen ? (t('login.logout') || 'Log Out') : undefined}
            aria-label={t('login.logout') || 'Log Out'}
          >
            <span className="sidebar-logout-icon">
              <FontAwesomeIcon icon={faRightFromBracket} className="me-0" />
            </span>
            {(!isCollapsed || isMobileOpen) && (
              <span className="sidebar-logout-label">
                {t('login.logout') || 'Log Out'}
              </span>
            )}
          </button>
        </div>

        {/* Function Keys Modal */}
        <FunctionKeysModal modalId="functionKeysModal" />
      </aside>

      {/* Backdrop for mobile drawer */}
      {isMobileOpen && (
        <div
          className="sidebar-backdrop d-md-none"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Sidebar;
